/**
 * Student Onboarding Page
 * Multi-step wizard for new students: DoB → Roster Link → Code of Conduct
 */

import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../lib/firebase';
import {
    collection,
    getDocs,
    doc,
    setDoc,
    updateDoc,
    addDoc,
    Timestamp,
    serverTimestamp
} from 'firebase/firestore';

interface RosterPlayer {
    id: string;
    firstName: string;
    lastName: string;
    grade: number;
    email?: string;
    linkedUserId?: string;
}

type OnboardingStep = 'dob' | 'roster' | 'conduct' | 'complete';

export default function StudentOnboardingPage() {
    const navigate = useNavigate();
    const { user, userDoc } = useAuth();

    const [step, setStep] = useState<OnboardingStep>('dob');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // DoB state
    const [birthDate, setBirthDate] = useState('');
    const [isUnder13, setIsUnder13] = useState(false);

    // Roster state
    const [rosterPlayers, setRosterPlayers] = useState<RosterPlayer[]>([]);
    const [matchingPlayers, setMatchingPlayers] = useState<RosterPlayer[]>([]);
    const [selectedPlayerId, setSelectedPlayerId] = useState<string | null>(null);
    const [showAddNew, setShowAddNew] = useState(false);

    // New player form
    const [newFirstName, setNewFirstName] = useState('');
    const [newLastName, setNewLastName] = useState('');
    const [newGrade, setNewGrade] = useState<number>(7);

    // Code of Conduct state
    const [conductAcknowledged, setConductAcknowledged] = useState(false);

    // Redirect if already onboarded or not logged in
    useEffect(() => {
        if (!user) {
            navigate('/login');
            return;
        }
        if (userDoc?.onboardingCompleted) {
            navigate('/app');
            return;
        }
    }, [user, userDoc, navigate]);

    // Load roster for matching
    useEffect(() => {
        async function loadRoster() {
            try {
                const snap = await getDocs(collection(db, 'playerRoster'));
                const players = snap.docs.map(d => ({
                    id: d.id,
                    ...d.data()
                } as RosterPlayer));
                setRosterPlayers(players);
            } catch (err) {
                console.error('Error loading roster:', err);
            }
        }
        loadRoster();
    }, []);

    // Calculate age from birth date
    function calculateAge(dateString: string): number {
        const today = new Date();
        const birth = new Date(dateString);
        let age = today.getFullYear() - birth.getFullYear();
        const monthDiff = today.getMonth() - birth.getMonth();
        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
            age--;
        }
        return age;
    }

    // Extract last name from email username
    function extractLastNameFromEmail(email: string): string {
        // ltanklam@peckschools.net → ltanklam → anklam (try removing 1-2 chars)
        const username = email.split('@')[0].toLowerCase();
        // Most usernames are 1-2 initials + last name
        // Return the part after first 1-2 characters
        return username.length > 2 ? username.slice(1) : username;
    }

    // Find matching roster entries by last name
    function findRosterMatches(email: string): RosterPlayer[] {
        const extracted = extractLastNameFromEmail(email);

        return rosterPlayers.filter(player => {
            const lastName = player.lastName.toLowerCase();
            // Check if extracted portion contains or matches last name
            return lastName === extracted ||
                extracted.includes(lastName) ||
                extracted.endsWith(lastName) ||
                // Also try removing 2 chars
                (email.split('@')[0].slice(2).toLowerCase().includes(lastName));
        }).filter(p => !p.linkedUserId); // Only show unlinked players
    }

    // Handle DoB submission
    async function handleDobSubmit(e: React.FormEvent) {
        e.preventDefault();
        setError(null);

        if (!birthDate) {
            setError('Please enter your date of birth');
            return;
        }

        const age = calculateAge(birthDate);
        const under13 = age < 13;
        setIsUnder13(under13);

        if (under13) {
            // Save DoB but mark as under 13
            setLoading(true);
            try {
                await setDoc(doc(db, 'users', user!.uid), {
                    birthDate,
                    birthYear: new Date(birthDate).getFullYear(),
                    isUnder13: true,
                    ageVerified: false,
                    updatedAt: serverTimestamp()
                }, { merge: true });
            } catch (err) {
                console.error('Error saving DoB:', err);
            } finally {
                setLoading(false);
            }
            // Stay on DoB step with under-13 message
            return;
        }

        // Over 13 - save and proceed
        setLoading(true);
        try {
            await setDoc(doc(db, 'users', user!.uid), {
                birthDate,
                birthYear: new Date(birthDate).getFullYear(),
                isUnder13: false,
                ageVerified: true,
                ageVerifiedAt: serverTimestamp(),
                updatedAt: serverTimestamp()
            }, { merge: true });

            // Find matching roster entries
            if (user?.email) {
                const matches = findRosterMatches(user.email);
                setMatchingPlayers(matches);
            }

            setStep('roster');
        } catch (err: any) {
            setError(err.message || 'Failed to save date of birth');
        } finally {
            setLoading(false);
        }
    }

    // Handle roster selection
    async function handleRosterSelect() {
        if (!selectedPlayerId && !showAddNew) {
            setError('Please select your name from the roster or add yourself');
            return;
        }

        setLoading(true);
        setError(null);

        try {
            if (showAddNew) {
                // Create new roster entry
                if (!newFirstName.trim() || !newLastName.trim()) {
                    setError('Please enter your first and last name');
                    setLoading(false);
                    return;
                }

                const newPlayer = await addDoc(collection(db, 'playerRoster'), {
                    firstName: newFirstName.trim(),
                    lastName: newLastName.trim(),
                    grade: newGrade,
                    email: user?.email || '',
                    linkedUserId: user!.uid,
                    games: {},
                    permissionSlipOnFile: false,
                    status: 'active',
                    season: 'Spring 2026',
                    createdAt: Timestamp.now(),
                    updatedAt: Timestamp.now()
                });

                // Link user to new player
                await setDoc(doc(db, 'users', user!.uid), {
                    linkedPlayerId: newPlayer.id,
                    linkedPlayerName: `${newFirstName.trim()} ${newLastName.trim()}`,
                    linkedAt: serverTimestamp(),
                    updatedAt: serverTimestamp()
                }, { merge: true });
            } else {
                // Link to existing roster entry
                const player = rosterPlayers.find(p => p.id === selectedPlayerId);
                if (!player) {
                    setError('Player not found');
                    setLoading(false);
                    return;
                }

                // Update roster entry with user link and email
                await updateDoc(doc(db, 'playerRoster', selectedPlayerId!), {
                    linkedUserId: user!.uid,
                    email: user?.email || '',
                    updatedAt: Timestamp.now()
                });

                // Update user with player link
                await setDoc(doc(db, 'users', user!.uid), {
                    linkedPlayerId: selectedPlayerId,
                    linkedPlayerName: `${player.firstName} ${player.lastName}`,
                    linkedAt: serverTimestamp(),
                    updatedAt: serverTimestamp()
                }, { merge: true });
            }

            setStep('conduct');
        } catch (err: any) {
            setError(err.message || 'Failed to link roster entry');
        } finally {
            setLoading(false);
        }
    }

    // Handle Code of Conduct acknowledgment
    async function handleConductComplete() {
        if (!conductAcknowledged) {
            setError('Please acknowledge the Code of Conduct');
            return;
        }

        setLoading(true);
        setError(null);

        try {
            await setDoc(doc(db, 'users', user!.uid), {
                onboardingCompleted: true,
                updatedAt: serverTimestamp()
            }, { merge: true });
            setStep('complete');
            // Redirect after short delay
            setTimeout(() => navigate('/app'), 1500);
        } catch (err: any) {
            setError(err.message || 'Failed to complete onboarding');
        } finally {
            setLoading(false);
        }
    }

    // Print Code of Conduct
    function handlePrint() {
        window.open('/documents/code-of-conduct-spring-2026.html', '_blank');
    }

    return (
        <div className="min-h-screen bg-secondary-900 py-8 px-4">
            <div className="max-w-2xl mx-auto">
                {/* Header */}
                <div className="text-center mb-8">
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-500 rounded-xl mb-4">
                        <span className="text-white text-3xl">🏴‍☠️</span>
                    </div>
                    <h1 className="text-3xl font-display font-bold text-white mb-2">
                        Welcome to Pirates Esports!
                    </h1>
                    <p className="text-secondary-400">
                        {step === 'dob' && "Let's get you set up"}
                        {step === 'roster' && "Link your player profile"}
                        {step === 'conduct' && "Review our Code of Conduct"}
                        {step === 'complete' && "You're all set!"}
                    </p>
                </div>

                {/* Progress Steps */}
                <div className="flex justify-center gap-2 mb-8">
                    {['dob', 'roster', 'conduct'].map((s, i) => (
                        <div
                            key={s}
                            className={`w-3 h-3 rounded-full transition-colors ${step === s ? 'bg-primary-500' :
                                ['dob', 'roster', 'conduct'].indexOf(step) > i ? 'bg-green-500' :
                                    'bg-secondary-700'
                                }`}
                        />
                    ))}
                </div>

                {/* Error Display */}
                {error && (
                    <div className="mb-6 p-4 bg-red-500/20 border border-red-500/50 rounded-lg">
                        <p className="text-red-400">{error}</p>
                    </div>
                )}

                {/* Step 1: Date of Birth */}
                {step === 'dob' && (
                    <div className="card">
                        {isUnder13 ? (
                            <div className="text-center py-8">
                                <div className="text-6xl mb-4">⚠️</div>
                                <h2 className="text-2xl font-bold text-white mb-4">
                                    Age Requirement Not Met
                                </h2>
                                <p className="text-secondary-300 mb-6 max-w-md mx-auto">
                                    The Peck Pirates Esports platform is only available to students
                                    aged 13 and older in compliance with COPPA regulations.
                                </p>
                                <p className="text-secondary-400 text-sm mb-6">
                                    Please come back when you turn 13! In the meantime, you can still
                                    view our public pages.
                                </p>
                                <Link to="/" className="btn-primary">
                                    Return to Home
                                </Link>
                            </div>
                        ) : (
                            <form onSubmit={handleDobSubmit}>
                                <h2 className="text-xl font-bold text-white mb-4">
                                    📅 Enter Your Date of Birth
                                </h2>
                                <p className="text-secondary-400 text-sm mb-6">
                                    We need to verify you're 13 or older to use this platform (COPPA compliance).
                                </p>

                                <div className="mb-6">
                                    <label htmlFor="birthDate" className="label">
                                        Date of Birth
                                    </label>
                                    <input
                                        type="date"
                                        id="birthDate"
                                        value={birthDate}
                                        onChange={(e) => setBirthDate(e.target.value)}
                                        max={new Date().toISOString().split('T')[0]}
                                        min="1900-01-01"
                                        required
                                        className="input"
                                    />
                                </div>

                                <button
                                    type="submit"
                                    disabled={loading || !birthDate}
                                    className="w-full btn-primary disabled:opacity-50"
                                >
                                    {loading ? 'Saving...' : 'Continue'}
                                </button>
                            </form>
                        )}
                    </div>
                )}

                {/* Step 2: Roster Linking */}
                {step === 'roster' && (
                    <div className="card">
                        <h2 className="text-xl font-bold text-white mb-4">
                            👤 Link Your Player Profile
                        </h2>
                        <p className="text-secondary-400 text-sm mb-6">
                            Select your name from the roster below, or add yourself if you're new.
                        </p>

                        {!showAddNew ? (
                            <>
                                {matchingPlayers.length > 0 ? (
                                    <div className="mb-6">
                                        <p className="text-sm text-green-400 mb-3">
                                            ✨ We found potential matches based on your email:
                                        </p>
                                        <div className="space-y-2">
                                            {matchingPlayers.map(player => (
                                                <label
                                                    key={player.id}
                                                    className={`flex items-center p-3 rounded-lg border cursor-pointer transition-colors ${selectedPlayerId === player.id
                                                        ? 'border-primary-500 bg-primary-500/10'
                                                        : 'border-secondary-700 hover:border-secondary-600'
                                                        }`}
                                                >
                                                    <input
                                                        type="radio"
                                                        name="player"
                                                        value={player.id}
                                                        checked={selectedPlayerId === player.id}
                                                        onChange={() => setSelectedPlayerId(player.id)}
                                                        className="mr-3"
                                                    />
                                                    <div>
                                                        <div className="font-medium text-white">
                                                            {player.firstName} {player.lastName}
                                                        </div>
                                                        <div className="text-sm text-secondary-400">
                                                            Grade {player.grade}
                                                        </div>
                                                    </div>
                                                </label>
                                            ))}
                                        </div>
                                    </div>
                                ) : (
                                    <p className="text-secondary-400 mb-4">
                                        We couldn't find a match for your email. Please select from the full roster or add yourself:
                                    </p>
                                )}

                                {/* Full roster dropdown for manual selection */}
                                <div className="mb-6">
                                    <label className="label">Or select from full roster:</label>
                                    <select
                                        value={selectedPlayerId || ''}
                                        onChange={(e) => setSelectedPlayerId(e.target.value || null)}
                                        className="input"
                                    >
                                        <option value="">-- Select your name --</option>
                                        {rosterPlayers
                                            .filter(p => !p.linkedUserId)
                                            .sort((a, b) => a.lastName.localeCompare(b.lastName))
                                            .map(player => (
                                                <option key={player.id} value={player.id}>
                                                    {player.lastName}, {player.firstName} (Grade {player.grade})
                                                </option>
                                            ))
                                        }
                                    </select>
                                </div>

                                <div className="flex gap-3">
                                    <button
                                        type="button"
                                        onClick={() => setShowAddNew(true)}
                                        className="btn-secondary flex-1"
                                    >
                                        ➕ I'm Not on the List
                                    </button>
                                    <button
                                        type="button"
                                        onClick={handleRosterSelect}
                                        disabled={loading || !selectedPlayerId}
                                        className="btn-primary flex-1 disabled:opacity-50"
                                    >
                                        {loading ? 'Linking...' : 'Continue'}
                                    </button>
                                </div>
                            </>
                        ) : (
                            <>
                                <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4 mb-6">
                                    <p className="text-blue-300 text-sm">
                                        📝 Adding yourself to the roster. Make sure to turn in your
                                        permission slip and Code of Conduct to Coach Anklam!
                                    </p>
                                </div>

                                <div className="space-y-4 mb-6">
                                    <div>
                                        <label htmlFor="firstName" className="label">First Name</label>
                                        <input
                                            type="text"
                                            id="firstName"
                                            value={newFirstName}
                                            onChange={(e) => setNewFirstName(e.target.value)}
                                            required
                                            className="input"
                                            placeholder="Enter your first name"
                                        />
                                    </div>
                                    <div>
                                        <label htmlFor="lastName" className="label">Last Name</label>
                                        <input
                                            type="text"
                                            id="lastName"
                                            value={newLastName}
                                            onChange={(e) => setNewLastName(e.target.value)}
                                            required
                                            className="input"
                                            placeholder="Enter your last name"
                                        />
                                    </div>
                                    <div>
                                        <label htmlFor="grade" className="label">Grade</label>
                                        <select
                                            id="grade"
                                            value={newGrade}
                                            onChange={(e) => setNewGrade(parseInt(e.target.value))}
                                            className="input"
                                        >
                                            {[6, 7, 8, 9, 10, 11, 12].map(g => (
                                                <option key={g} value={g}>Grade {g}</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>

                                <div className="flex gap-3">
                                    <button
                                        type="button"
                                        onClick={() => setShowAddNew(false)}
                                        className="btn-secondary flex-1"
                                    >
                                        ← Back
                                    </button>
                                    <button
                                        type="button"
                                        onClick={handleRosterSelect}
                                        disabled={loading || !newFirstName.trim() || !newLastName.trim()}
                                        className="btn-primary flex-1 disabled:opacity-50"
                                    >
                                        {loading ? 'Adding...' : 'Add & Continue'}
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                )}

                {/* Step 3: Code of Conduct */}
                {step === 'conduct' && (
                    <div className="card">
                        <h2 className="text-xl font-bold text-white mb-4">
                            📜 Code of Conduct
                        </h2>
                        <p className="text-secondary-400 text-sm mb-6">
                            Please review our Code of Conduct and print a copy to sign and bring to class.
                        </p>

                        <div className="bg-secondary-800 rounded-lg p-4 mb-6 max-h-64 overflow-y-auto">
                            <h3 className="font-bold text-white mb-3">Peck Pirates Esports Code of Conduct</h3>
                            <div className="text-secondary-300 text-sm space-y-3">
                                <p><strong>1. Respect:</strong> Treat all players, coaches, and opponents with respect. No bullying, harassment, or toxic behavior.</p>
                                <p><strong>2. Sportsmanship:</strong> Win with humility, lose with grace. Always shake hands (virtually or in-person) after matches.</p>
                                <p><strong>3. Academic Priority:</strong> Academics come first. Maintain passing grades in all classes.</p>
                                <p><strong>4. Attendance:</strong> Attend practices and competitions regularly. Communicate absences in advance.</p>
                                <p><strong>5. Equipment:</strong> Treat school equipment with care. Report any issues immediately.</p>
                                <p><strong>6. Online Behavior:</strong> Represent Peck positively online. No inappropriate content or language.</p>
                                <p><strong>7. Communication:</strong> Use Discord responsibly. Keep conversations appropriate and on-topic.</p>
                            </div>
                        </div>

                        <button
                            type="button"
                            onClick={handlePrint}
                            className="w-full btn-secondary mb-4"
                        >
                            🖨️ Print Full Code of Conduct
                        </button>

                        <label className="flex items-start gap-3 p-4 bg-secondary-800 rounded-lg mb-6 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={conductAcknowledged}
                                onChange={(e) => setConductAcknowledged(e.target.checked)}
                                className="mt-1"
                            />
                            <span className="text-secondary-300 text-sm">
                                I have read and agree to follow the Peck Pirates Esports Code of Conduct.
                                I understand I need to print, sign, and return a physical copy to Coach Anklam.
                            </span>
                        </label>

                        <button
                            type="button"
                            onClick={handleConductComplete}
                            disabled={loading || !conductAcknowledged}
                            className="w-full btn-primary disabled:opacity-50"
                        >
                            {loading ? 'Completing...' : 'Complete Setup'}
                        </button>
                    </div>
                )}

                {/* Complete */}
                {step === 'complete' && (
                    <div className="card text-center py-12">
                        <div className="text-6xl mb-4">🎉</div>
                        <h2 className="text-2xl font-bold text-white mb-4">
                            Welcome Aboard, Pirate!
                        </h2>
                        <p className="text-secondary-300 mb-6">
                            Your account is all set up. Redirecting to your dashboard...
                        </p>
                        <div className="w-8 h-8 border-4 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
                    </div>
                )}
            </div>
        </div>
    );
}
